import { Box, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import React from 'react';
import { searchInputKeyboardShortcuts } from '../../keyboardShortcuts';


// Add the style for the animation once when the module is loaded
const pulseAnimation = `
@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
  100% {
    opacity: 1;
  }
}`;

// Use an IIFE to add the style to the document only once
(function addStyleOnce() {
  if (!document.getElementById('pulse-animation-style')) {
    const styleElement = document.createElement('style');
    styleElement.id = 'pulse-animation-style';
    styleElement.innerHTML = pulseAnimation;
    document.head.appendChild(styleElement);
  }
})();

export interface TableToolbarProps {
  title?: string | React.ReactNode;
  numSelected: number;
  filterValue?: string;
  autoRefreshActive?: boolean;
  selectActionBar?: React.ReactNode;
  actionBar?: React.ReactNode;
  onRequestFilter: (filter?: string) => void;
  refresh?: () => void;
}

function TableToolbar(props: TableToolbarProps) {

  const searchRef = React.useRef<HTMLInputElement>(null);
  searchInputKeyboardShortcuts(searchRef);

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={[
        {
          pl: { sm: 1 },
          pr: { xs: 0.5, sm: 0.5 },
          minHeight: '40px !important',
          py: 0.5,
          flexWrap: { xs: 'wrap', md: 'nowrap' },
        },
        props.numSelected > 0 && {
          bgcolor: (theme) =>
            theme.palette.mode === 'light'
              ? 'rgba(25, 118, 210, 0.12)'
              : 'rgba(25, 118, 210, 0.16)',
        },
      ]}
    >
      {props.numSelected > 0 ? (
        <>
          <Typography
            sx={{ flex: '1 1 100%', fontSize: '0.875rem' }}
            color="inherit"
            variant="subtitle2"
            component="div"
          >
            {props.numSelected} selected
          </Typography>
          {props.selectActionBar}
        </>
      ) : (
        <>
          <Typography
            sx={{ flex: '1 1 100%' }}
            variant="h6"
            id="tableTitle"
            component="div"
          >
            {props.title}
          </Typography>

          {props.autoRefreshActive && (
            <Typography
              variant="caption"
              sx={{
                ml: 1,
                display: 'inline-flex',
                alignItems: 'center',
                color: 'text.secondary',
                animation: 'pulse 2s infinite',
                fontSize: '0.7rem'
              }}
            >
              <Box
                component="span"
                sx={{
                  height: 6,
                  width: 6,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  mr: 0.5,
                  display: 'inline-block'
                }}
              />
              Auto-refreshing...
            </Typography>
          )}

          {props.refresh && (
            <IconButton
              onClick={props.refresh}
              size="small"
              sx={{ ml: 0.5, p: 0.5 }}
              title="Refresh list">
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}

          <Tooltip title="Search list (Alt+S)">
            <TextField
              inputRef={searchRef}
              placeholder="Search"
              margin="none"
              size="small"
              defaultValue={props.filterValue || undefined}
              onFocus={(event) => event.target.select()}
              onBlur={(event) => props.onRequestFilter(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  props.onRequestFilter((event.target as HTMLInputElement).value);
                } else if (event.key === 'Escape') {
                  (event.target as HTMLInputElement).value = '';
                  props.onRequestFilter('');
                }
              }}
              sx={{
                width: { xs: '150px', sm: '250px' },
                ml: 1,
                '& .MuiInputBase-root': {
                  height: '28px',
                  fontSize: '0.8125rem'
                },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#ffffff',
                  },
                  '&:hover fieldset': {
                    borderColor: '#ffffff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#ffffff',
                  },
                },
                '& .MuiInputBase-input': {
                  color: '#ffffff',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#ffffff',
                  opacity: 0.7,
                }
              }}
            />
          </Tooltip>
          <Typography variant="caption" sx={{ ml: 1, fontSize: '0.7rem', color: '#ffffff', display: { xs: 'none', sm: 'block' }, whiteSpace: 'nowrap' }}>
            -term exclude · `term` exact
          </Typography>
          {props.actionBar}
        </>
      )
      }
    </Stack >
  );
}

export default TableToolbar;